import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Loader2Icon, SendIcon, SparklesIcon } from 'lucide-react';
import { InfoTip } from '../../ui/InfoTip';
import type { ProtectAiResult } from '../../../types/protect';
type Phase = 'idle' | 'typing' | 'ready' | 'thinking' | 'done';
type AiProtectPromptProps = {
  onRun: () => void;
  result: ProtectAiResult | null;
};
const SUGGESTION = 'Don’t put supports on the cogs';
const STEPS = ['Understanding your request', 'Scanning model geometry', 'Detecting circular gear features', 'Selecting matching surfaces'];
const STEP_MS = 650;
export function AiProtectPrompt({
  onRun,
  result
}: AiProtectPromptProps) {
  const [text, setText] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [step, setStep] = useState(0);
  const [regions, setRegions] = useState(0);
  const timers = useRef<number[]>([]);
  const startResultId = useRef<number | null>(null);
  const autofilled = useRef(false);
  useEffect(() => () => timers.current.forEach(t => window.clearTimeout(t)), []);
  useEffect(() => {
    if (phase !== 'thinking' || !result || result.id === startResultId.current) return;
    setRegions(result.regions);
    setStep(STEPS.length);
    setPhase('done');
  }, [result, phase]);
  const autofill = () => {
    if (autofilled.current || phase !== 'idle' || text) return;
    autofilled.current = true;
    setPhase('typing');
    let i = 0;
    const tick = () => {
      i++;
      setText(SUGGESTION.slice(0, i));
      if (i < SUGGESTION.length) timers.current.push(window.setTimeout(tick, 28));else setPhase('ready');
    };
    timers.current.push(window.setTimeout(tick, 120));
  };
  const submit = () => {
    if (!text.trim() || phase === 'thinking' || phase === 'typing') return;
    startResultId.current = result?.id ?? null;
    setPhase('thinking');
    setStep(0);
    STEPS.forEach((_, i) => {
      if (i === 0) return;
      timers.current.push(window.setTimeout(() => setStep(i), i * STEP_MS));
    });
    timers.current.push(window.setTimeout(onRun, (STEPS.length - 1) * STEP_MS + 300));
  };
  const canSend = !!text.trim() && phase !== 'thinking' && phase !== 'typing';
  return <div data-id="element-450" data-mp="120k127oiimi0">
      <div className="flex items-center gap-[6px] pb-[6px] pt-[10px]" data-id="element-451" data-mp="120sdxzkmxrt7">
        <h3 className="text-[13.5px] font-bold text-pf-ink" data-id="element-452" data-mp="16z3o6beue4by">Describe areas to protect (AI)</h3>
        <InfoTip label="About AI selection" data-id="element-453" data-mp="1bw3vepnfhd4n" />
      </div>
      <form onSubmit={e => {
      e.preventDefault();
      submit();
    }} className={`flex h-[44px] items-center gap-[8px] rounded-[6px] border bg-white pl-[10px] pr-[5px] transition-colors duration-150 ${phase === 'thinking' ? 'border-pf-blue' : 'border-pf-blue-line'}`} data-id="element-454" data-mp="1gv6pjf1vdyxu">
        
        <SparklesIcon className="h-[15px] w-[15px] shrink-0 text-pf-ink" strokeWidth={2} fill="currentColor" data-id="element-455" data-mp="1uoft2z34zq8g" />
        <input aria-label="Describe areas to protect" value={text} readOnly={phase === 'typing' || phase === 'thinking'} onChange={e => {
        setText(e.target.value);
        if (phase === 'done' || phase === 'idle') setPhase(e.target.value ? 'ready' : 'idle');
      }} onFocus={autofill} onClick={autofill} placeholder="E.g. “Protect the front face, all text, and the top surface”" className="min-w-0 flex-1 bg-transparent text-[11.5px] text-pf-ink outline-none placeholder:text-pf-muted" data-id="element-456" data-mp="1zmpuhrfgfn2i" />
        
        <button type="submit" aria-label="Select with AI" disabled={!canSend} className={`flex h-[32px] w-[34px] shrink-0 items-center justify-center rounded-[6px] text-white transition-colors duration-150 ${canSend ? 'bg-pf-blue hover:bg-pf-blue-hover' : 'bg-[#a9cdf7]'}`} data-id="element-457" data-mp="2nibarp44m0hp">
          
          {phase === 'thinking' ? <Loader2Icon className="h-[14px] w-[14px] animate-spin" data-id="element-458" data-mp="36fcg5b1le2m6" /> : <SendIcon className="h-[14px] w-[14px]" strokeWidth={2} data-id="element-459" data-mp="2qydx7r4l6d79" />}
        </button>
      </form>

      <AnimatePresence mode="wait" initial={false} data-id="element-460" data-mp="2vxh3fewaypgt">
        {phase === 'thinking' || phase === 'done' ? <motion.ol key="steps" initial={{
        opacity: 0,
        y: -3
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-[8px] flex flex-col gap-[5px] rounded-[6px] bg-white px-[10px] py-[8px] ring-1 ring-pf-line" aria-live="polite" data-id="element-461" data-mp="2hxz80goi4dyh">
          
            {STEPS.map((label, i) => {
          const done = step > i;
          const active = step === i && phase === 'thinking';
          if (i > step && phase === 'thinking') return null;
          return <motion.li key={label} initial={{
            opacity: 0,
            x: -4
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }} className={`flex items-center gap-[7px] text-[11.5px] ${active ? 'text-pf-ink' : 'text-pf-muted'}`} data-id="element-462" data-mp="3snzoubn8xm6k" data-mpi={i}>
                
                  <span className={`flex h-[12px] w-[12px] items-center justify-center rounded-full ${done ? 'bg-pf-blue text-white' : 'text-pf-blue'}`} data-id="element-463" data-mp="1h04fo3g1njvc">
                    {done ? <CheckIcon className="h-[8px] w-[8px]" strokeWidth={3.5} data-id="element-464" data-mp="1qr8r4t6igmlf" /> : <Loader2Icon className="h-[11px] w-[11px] animate-spin" strokeWidth={2.5} data-id="element-465" data-mp="130mwq4pfj0tw" />}
                  </span>
                  {label}
                  {active && '…'}
                </motion.li>;
        })}
            {phase === 'done' && <li className="mt-[2px] border-t border-pf-line pt-[6px] text-[11.5px] font-semibold text-pf-ink" data-id="element-466" data-mp="1czkjp377imas">
                {regions > 0 ? `Protected ${regions} cog ${regions === 1 ? 'region' : 'regions'} on the model` : 'No matching surfaces found — try painting them instead'}
              </li>}
          </motion.ol> : <motion.p key="hint" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="pt-[7px] text-[11px] text-pf-muted" data-id="element-467" data-mp="2jh7k2zntt3vd">
            The AI will automatically select the matching surfaces on your model.
          </motion.p>}
      </AnimatePresence>
    </div>;
}